import React from 'react';
import { Box, Grid } from '@mui/material';
import useMediaQuery from '@mui/system/useMediaQuery';
import colors from '../../theme/colors';
import theme, { breakpoints } from '../../theme';
import * as T from '../Typography';
import { SurveyProgressWrapper, IconWrapper } from './style';
import { Check } from '@mui/icons-material';
import SectionProgressCircle from './SectionProgressCircle';

import { routes } from '../../constants';

interface SectionDetails {
  sectionLabel: string;
  questions: number;
  answers: number;
  sectionOrder: number;
}

interface SurveyProgressProps {
  sectionDetails: SectionDetails[];
  totalQuestions: number;
  totalAnswers: number;
}

const SurveyProgress: React.FC<SurveyProgressProps> = ({
  sectionDetails,
  totalQuestions,
  totalAnswers
}) => {
  const isWideScreen = useMediaQuery(`(min-width:${breakpoints.tablet}px)`);

  const sectionsList = sectionDetails
    .map(({ sectionLabel, questions, answers, sectionOrder }) => ({
      title: sectionLabel,
      questionsCount: questions,
      answersCount: answers,
      sectionUrl: `${routes.SURVEY}?sectionOrder=${sectionOrder}`,
      sectionNumber: sectionOrder,
      status:
        answers >= questions
          ? 'done'
          : answers > 0
            ? 'in progress'
            : 'not started'
    }))
    .sort((a, b) => a.sectionNumber - b.sectionNumber);

  const renderSectionStatus = (
    status: string,
    answersCount: number,
    questionsCount: number,
    sectionNumber: number
  ) => {
    if (sectionNumber === -1 && status !== 'done') {
      return 'B';
    }

    if (status === 'done') {
      return <Check color="inherit" style={{ marginBottom: -5 }} />;
    }

    if (status === 'in progress') {
      const percentage = (answersCount / questionsCount) * 100;
      return (
        <SectionProgressCircle
          percentage={percentage}
          sectionNumber={sectionNumber}
        />
      );
    }

    return sectionNumber;
  };

  const renderSectionLink = (status: string, sectionUrl: string) => {
    const text =
      status === 'done'
        ? 'Review'
        : status === 'in progress'
          ? 'Continue'
          : 'Get started';

    return (
      <T.Link
        color={theme.colors.blue.shades.shade700}
        weight={'bold'}
        decoration={'none'}
        to={sectionUrl}
      >
        <T.TextSmallBold color={colors.primary.shades.shade900}>
          {text}
        </T.TextSmallBold>
      </T.Link>
    );
  };

  return (
    <Grid container flexDirection="column">
      <Box flex={1} display="flex" justifyContent="space-between" mb={2}>
        <T.H4 color={theme.colors.neutral.shades.shade700}>
          SURVEY PROGRESS
        </T.H4>
      </Box>

      {sectionsList.map(
        ({
          title,
          questionsCount,
          answersCount,
          sectionUrl,
          sectionNumber,
          status
        }) => (
          <SurveyProgressWrapper
            data-testid="survey-progress-section"
            key={sectionNumber}
            flexDirection={isWideScreen ? 'row' : 'column'}
            cardBackground={theme.colors.neutral.shades.shade100}
          >
            <IconWrapper status={status}>
              <T.TextSmallBold color="inherit">
                {renderSectionStatus(
                  status,
                  answersCount,
                  questionsCount,
                  sectionNumber
                )}
              </T.TextSmallBold>
            </IconWrapper>
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              mb={isWideScreen ? 0 : 3}
            >
              <T.TextSmallBold
                color={colors.neutral.shades.shade700}
                ta={isWideScreen ? 'left' : 'center'}
              >
                {title}
              </T.TextSmallBold>
              <Box display="flex" alignItems="center">
                <T.TextExtraSmallBold
                  color={colors.neutral.shades.shade500}
                  mr={1}
                >
                  {answersCount}/{questionsCount}
                </T.TextExtraSmallBold>
                <T.TextExtraSmallRegular
                  color={colors.neutral.shades.shade600}
                  ta={isWideScreen ? 'left' : 'center'}
                >
                  questions answered.
                </T.TextExtraSmallRegular>
              </Box>
            </Box>
            <Box>{renderSectionLink(status, sectionUrl)}</Box>
          </SurveyProgressWrapper>
        )
      )}
    </Grid>
  );
};

export default SurveyProgress;
